import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      
      // Only redirect if not already on login page to avoid infinite reload
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication APIs
export const login = (credentials) => {
  return api.post('/auth/login', credentials);
};

// export const loginUser = (credentials) => {
//   return api.post('/auth/login', credentials);
// };

export const registerLaboratory = (labData) => {
  return api.post('/auth/register', labData);
};

export const sendOTP = (data) => {
  return api.post('/auth/send-otp', data);
};

export const verifyOTP = (data) => {
  return api.post('/auth/verify-otp', data);
};

export const changePassword = (data) => {
  return api.post('/auth/change-password', data);
};

// Location APIs
export const getCountries = () => {
  return api.get('/location/countries');
};

export const getStates = () => {
  return api.get(`/laboratory/states`);
};

export const getCities = (stateId) => {
  return api.get(`/laboratory/cities/${stateId}`);
};

// Laboratory APIs
export const getAllLabs = () => {
  return api.get('/calibration-lab/labs');
};

export const getLabById = (orgId) => {
  return api.get(`/calibration-lab/labs/${orgId}`);
};

export const saveLabBasicDetails = (labData) => {
  return api.post('/calibration-lab/basic-details', labData);
};

export const updateLab = (orgId, labData) => {
  return api.put(`/calibration-lab/labs/${orgId}`, labData);
};

export const deleteLab = (orgId) => {
  return api.delete(`/calibration-lab/labs/${orgId}`);
};

// User APIs
export const getAllUsers = () => {
  return api.get('/users');
};

export const getUserById = (id) => {
  return api.get(`/users/${id}`);
};

export const createUser = (userData) => {
  return api.post('/users', userData);
};

export const updateUser = (id, userData) => {
  return api.put(`/users/${id}`, userData);
};

export const deleteUser = (id) => {
  return api.delete(`/users/${id}`);
};

// Declaration APIs
export const getApplicationId = (orgId) => {
  return api.post('/declaration/get-app-id', { orgId });
};

export const getCurrentStage = (appId) => {
  return api.post('/declaration/get-current-stage', { appId });
};

export const submitDeclaration = (data) => {
  return api.post('/declaration/submit', data);
};

export const getDeclarationStatus = (orgId) => {
  return api.get(`/declaration/status/${orgId}`);
};

// Application Form APIs
export const getApplicationForm = (labTypeId, orgId) => {
  return api.get(`/application/form?labTypeId=${labTypeId}&orgId=${orgId}`);
};

export const saveApplicationForm = (data) => {
  return api.post('/application/save', data);
};

export const submitApplication = (data) => {
  return api.post('/application/submit', data);
};

// Document Adequacy APIs
export const getAssessmentData = (capacity) => {
  return api.get(`/document-adequacy/assessment?capacity=${capacity || 'LA'}`);
};

export const getAssessorInfo = () => {
  return api.get('/document-adequacy/assessor');
};

export const checkAdequacyStatus = () => {
  return api.get('/document-adequacy/adequacy-status');
};

export const saveAssessmentAnswer = (data) => {
  return api.post('/document-adequacy/save-answer', data);
};

export const submitDAResponse = (data) => {
  return api.post('/document-adequacy/submit-response', data);
};

export const uploadExcelData = (data) => {
  return api.post('/document-adequacy/upload-excel', data);
};

// DAQMS Sub Category APIs
export const getDAQMSSubCategories = (filters = {}) => {
  const params = new URLSearchParams();
  
  // Default to Medical field if not specified
  const field = filters.field || 'Testing';
  params.append('field', field);
  
  if (filters.standard) params.append('standard', filters.standard);
  if (filters.catId) params.append('catId', filters.catId);
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/daqms-subcategories${queryString ? '?' + queryString : ''}`);
};

export const getDAQMSSubCategoryById = (id) => {
  return api.get(`/document-adequacy/daqms-subcategories/${id}`);
};

export const getDAQMSFields = () => {
  return api.get('/document-adequacy/daqms-fields');
};

export const getDAQMSStandards = () => {
  return api.get('/document-adequacy/daqms-standards');
};

// DAQMS Category APIs
export const getDAQMSCategories = (filters = {}) => {
  const params = new URLSearchParams();
  
  // Default to Medical field if not specified
  const field = filters.field || 'Testing';
  params.append('field', field);
  
  if (filters.standard) params.append('standard', filters.standard);
  if (filters.displayId) params.append('displayId', filters.displayId);
  if (filters.disId) params.append('disId', filters.disId);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/daqms-categories${queryString ? '?' + queryString : ''}`);
};

export const getDAQMSCategoryById = (id) => {
  return api.get(`/document-adequacy/daqms-categories/${id}`);
};

export const getDAQMSCategoryNames = (field = 'Testing') => {
  return api.get(`/document-adequacy/daqms-category-names?field=${field}`);
};

// DAQMS Question APIs
export const getDAQMSQuestions = (filters = {}) => {
  const params = new URLSearchParams();
  
  // Default to Medical field and 2022 standard if not specified
  const field = filters.field || 'Testing';
  const standard = filters.standard || '2017';
  
  params.append('field', field);
  params.append('standard', standard);
  
  if (filters.subCategoryId) params.append('subCategoryId', filters.subCategoryId);
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/daqms-questions${queryString ? '?' + queryString : ''}`);
};

export const getDAQMSQuestionById = (id) => {
  return api.get(`/document-adequacy/daqms-questions/${id}`);
};

// NC Statements API
export const getNCStatements = (filters = {}) => {
  const params = new URLSearchParams();
  
  // Default to Medical field and 2022 standard if not specified
  const field = filters.field || 'Testing';
  const standard = filters.standard || '2017';
  
  params.append('field', field);
  params.append('standard', standard);
  
  if (filters.clauseNo) params.append('clauseNo', filters.clauseNo);
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/nc-statements${queryString ? '?' + queryString : ''}`);
};

// Implementation Data API (from Compliance_Requirements table)
export const getImplementationData = (filters = {}) => {
  const params = new URLSearchParams();
  
  // Default to Medical field and 2022 standard if not specified
  const field = filters.field || 'Testing';
  const standard = filters.standard || '2017';
  
  params.append('field', field);
  params.append('standard', standard);
  
  if (filters.clauseNo) params.append('clauseNo', filters.clauseNo);
  if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/implementation-data${queryString ? '?' + queryString : ''}`);
};

// Templates Data API (from ListFormats table)
export const getTemplatesData = (filters = {}) => {
  const params = new URLSearchParams();
  
  // Default to Medical field and 2022 standard if not specified
  const field = filters.field || 'Testing';
  const standard = filters.standard || '2017';
  
  params.append('field', field);
  params.append('standard', standard);
  
  if (filters.clauseNo) params.append('clauseNo', filters.clauseNo);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/templates-data${queryString ? '?' + queryString : ''}`);
};

// Assessment Remarks APIs
export const saveAssessmentRemark = (remarkData) => {
  return api.post('/document-adequacy/save-assessment-remark', remarkData);
};

export const getAssessmentRemarks = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.appId) params.append('appId', filters.appId);
  if (filters.orgId) params.append('orgId', filters.orgId);
  if (filters.assessmentId) params.append('assessmentId', filters.assessmentId);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/assessment-remarks${queryString ? '?' + queryString : ''}`);
};

// Evidence APIs
export const saveEvidence = (evidenceData) => {
  return api.post('/document-adequacy/save-evidence', evidenceData);
};

export const getEvidence = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.appId) params.append('appId', filters.appId);
  if (filters.orgId) params.append('orgId', filters.orgId);
  if (filters.assessmentId) params.append('assessmentId', filters.assessmentId);
  if (filters.ncItem) params.append('ncItem', filters.ncItem);
  if (filters.clauseNo) params.append('clauseNo', filters.clauseNo);
  
  const queryString = params.toString();
  return api.get(`/document-adequacy/evidence${queryString ? '?' + queryString : ''}`);
};

export const getDAQMSEvidence = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.appId) params.append('appId', filters.appId);
  if (filters.orgId) params.append('orgId', filters.orgId);
  if (filters.assessmentId) params.append('assessmentId', filters.assessmentId);
  if (filters.assessorId) params.append('assessorId', filters.assessorId);
  if (filters.labTypeId) params.append('labTypeId', filters.labTypeId);
  if (filters.clause) params.append('clause', filters.clause);
  if (filters.standard) params.append('standard', filters.standard);
  
  const queryString = params.toString();
  return api.get(`/assessment/get-daqms-evidence${queryString ? '?' + queryString : ''}`);
};

export const getDANCwithClauseNo = (orgid, appid) => {
  return api.get(`/da-nc/${orgid}/${appid}`);
};

// DA NC (Document Adequacy Non-Compliance) APIs
export const insertDANC = (data) => {
  return api.post('/da-nc', data);
};

export const getDANC = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.orgid) params.append('orgid', filters.orgid);
  if (filters.appid) params.append('appid', filters.appid);
  if (filters.assessorId) params.append('assessorId', filters.assessorId);
  if (filters.sectionName) params.append('sectionName', filters.sectionName);
  if (filters.subsectionName) params.append('subsectionName', filters.subsectionName);
  if (filters.informationStatus) params.append('informationStatus', filters.informationStatus);
  if (filters.isactive !== undefined) params.append('isactive', filters.isactive);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.ClauseNo) params.append('ClauseNo', filters.ClauseNo);
  if (filters.offset) params.append('offset', filters.offset);
  
  const queryString = params.toString();
  return api.get(`/da-nc${queryString ? '?' + queryString : ''}`);
};
export const getDANCForFinalSubmit = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.orgid) params.append('orgid', filters.orgid);
  if (filters.appid) params.append('appid', filters.appid);
  if (filters.informationStatus) params.append('informationStatus', filters.informationStatus);
  if (filters.isactive !== undefined) params.append('isactive', filters.isactive);
  const queryString = params.toString();
  return api.get(`/da-nc/forfinal${queryString ? '?' + queryString : ''}`);
};
export const getDANCForFinalSubmitCalibration = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.orgid) params.append('orgid', filters.orgid);
  if (filters.appid) params.append('appid', filters.appid);
  if (filters.informationStatus) params.append('informationStatus', filters.informationStatus);
  if (filters.isactive !== undefined) params.append('isactive', filters.isactive);
  const queryString = params.toString();
  return api.get(`/da-nc/forfinal-calibration${queryString ? '?' + queryString : ''}`);
};

export const getDANCById = (orgid, appid, sectionName) => {
  return api.get(`/da-nc/${orgid}/${appid}/${encodeURIComponent(sectionName)}`);
};
export const getDANCByIdEnclosure = (orgid, appid, sectionName) => {
  return api.get(`/da-nc/enclosure/${orgid}/${appid}/${encodeURIComponent(sectionName)}`);
};

export const updateDANC = (orgid, appid, sectionName, data) => {
  return api.put(`/da-nc/${orgid}/${appid}/${encodeURIComponent(sectionName)}`, data);
};

export const updateDANCBySourceId = (orgid, appid, sectionName, sourceid, assessorId, assessmentId, data) => {
  return api.put(`/da-nc/${orgid}/${appid}/${encodeURIComponent(sectionName)}/${sourceid}/${assessorId}/${assessmentId}`, data);
};

export const bulkUpsertDANC = (orgid, appid, sectionName, assessmentId, assessorId, items) => {
  return api.put('/da-nc/bulk-upsert', { orgid, appid, sectionName, assessmentId, assessorId, items });
};

export const updateDANCBySourceIdEditCertificate = (orgid, appid, sectionName, sourceid, assessorId, assessmentId, data) => {
  return api.put(`/da-nc/edit-certificate/${orgid}/${appid}/${encodeURIComponent(sectionName)}/${sourceid}/${assessorId}/${assessmentId}`, data);
};

export const updateDANCBySourceIdNewAppType = (orgid, appid, sectionName, sourceid, assessorId, assessmentId, data) => {
  return api.put(`/da-nc/new-app-type/${orgid}/${appid}/${encodeURIComponent(sectionName)}/${sourceid}/${assessorId}/${assessmentId}`, data);
};

export const deleteDANC = (orgid, appid, sectionName) => {
  return api.delete(`/da-nc/${orgid}/${appid}/${encodeURIComponent(sectionName)}`);
};

export const getDANCGroupedBySection = (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.orgid) params.append('orgid', filters.orgid);
  if (filters.appid) params.append('appid', filters.appid);
  if (filters.isactive !== undefined) params.append('isactive', filters.isactive);
  
  const queryString = params.toString();
  return api.get(`/da-nc/grouped-by-section${queryString ? '?' + queryString : ''}`);
};

export const getAuditTrailLogs = (orgid, appid) => {
  return api.get(`/da-nc/audit-trail?orgid=${orgid}&appid=${appid}`);
};

export const getNCChecklist = (params) => {
  const queryString = new URLSearchParams(params).toString();
  return api.get(`/da-nc/ncchecklist?${queryString}`);
};

export default api;
